import { defineComponent as _defineComponent } from 'vue'
import productRegistrationService from '@/services/product-registration.service';
import productRegistrationActionService from './product-registration-action.service';
import { Action } from './registeredproductaction.interface';
import ProductTileImageCarousel from '@/ui/shared/product-tile/ProductTileImageCarousel.vue';
import ProductRegistrationAddOrUpdateProductWebRequest = Vertica.LouisPoulsen.Application.ProductRegistration.Requests.ProductRegistrationAddOrUpdateProductWebRequest;
import ProductDetails = Vertica.LouisPoulsen.Application.ProductRegistration.Responses.ProductRegistrationProductDetailsWebResponse.ProductDetails;
import RegisteredProductViewObject = Vertica.LouisPoulsen.Application.ProductRegistration.Responses.RegisteredProductViewObject;
import { computed, onMounted, ref, watch } from 'vue';
import { useValidator } from '@/core/app.service';
import ReceiptUpload from './ReceiptUpload.vue';
import ImageUpload from '@/ui/shared/imageupload/ImageUpload.vue';
import ImageUploadService from '@/ui/shared/imageupload/image-upload.service';
import CollapsibleContent from './CollapsibleContent.vue';
import { useTranslations } from '@/core/translate.mixin';
import { router } from '@/router';
import PageService from '@/core/page.service';
import { ProductRegistrationMode } from '@/enums';
import dictionaryService from '@/core/dictionary.service';

interface IColorOption {
    id: string;
    displayName: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'RegisterProductPage',
  props: {
    editMode: { type: Boolean },
    registeredProductPageUrl: null
  },
  setup(__props: any) {

const props = __props as {
    editMode: boolean,
    registeredProductPageUrl: string
}



const { t } = useTranslations();
const OTHER : IColorOption = { id: 'Other', displayName: t('Pages.ProductRegistration.RegisterProductDetailsPage.CustomColour') };

const activeState = computed<RegisterProductViewModel | null>(() => props.editMode ? null : productRegistrationService.getViewModel());

const createdViaSerialNumber = computed<boolean>(() => activeState.value && activeState.value.mode === ProductRegistrationMode.SerialNumber);

const productOptions = computed<ProductRegistrationProductTileViewModel[]>(() => props.editMode
    ? editCatalogProduct.value ? [editCatalogProduct.value] : []
    : activeState.value.productOptions
);

const editProductId = ref<string>(null);
const editProduct = ref<RegisteredProductViewObject>();
const editCatalogProduct = ref<ProductRegistrationProductTileViewModel>(null);

const initialReceipt = ref<CarouselImageWithExtension>();
const editReceipt = ref<CarouselImageWithExtension>();
const initialImages = ref<CarouselImageWithExtension[]>([]);
const editImages = ref<CarouselImageWithExtension[]>([]);

const receiptUploadComponent = ref(null);
const imageUploadComponent = ref(null);
const imageFiles = ref<FileList | null>(null);

const activeProductOption = ref<ProductRegistrationProductTileViewModel | null>(null);

function getColorOptions(product: ProductRegistrationProductTileViewModel) : IColorOption[] {
    return product && productDetails.value[activeProductOption.value.productId]
        ? productDetails.value[product.productId].colors.map(c => ({ id: c, displayName: c })).concat(OTHER)
        : [OTHER];
}
const selectedColor = ref<IColorOption>(OTHER);

const selectedProductDealer = computed<string>(() => form.value.productDealer || t('Pages.ProductRegistration.RegisterProductDetailsPage.DealerLabel'));
const title = computed<string>(() => props.editMode ? t('Pages.ProductRegistration.EditProductDetailsPage.Title') : t('Pages.ProductRegistration.RegisterProductDetailsPage.Title'));


const $validator = useValidator();
$validator.extend(
    'image_filesize', {
        getMessage: _ => dictionaryService.get('Pages.ProductRegistration.RegisterProductDetailsPage.ImageFileSizeErrorMessage'),
        validate: _ => {
            const files = imageFiles.value;
            for (let i = 0; i < files.length; i++) {
                if (files[i].size >= 3 * 1024 * 1024) { return false; }
            }
            return true;
        }
    });

const form = ref<ProductRegistrationAddOrUpdateProductWebRequest>(null);

const topImageUrl = computed<string>(() => props.editMode ? (editProduct.value.image ? editProduct.value.image.url : null) : activeProductOption.value.defaultImageUrl);

const request = computed<ProductRegistrationAddOrUpdateProductWebRequest>(() => {
    return form.value
        ? {
            name: form.value.name,
            dateBought: form.value.dateBought,
            colour: selectedColor.value === OTHER ? customColour.value : selectedColor.value.id,
            comment: form.value.comment,
            productDealer: useCustomDealer.value ? customDealer.value : form.value.productDealer,
            sapProductId: form.value.sapProductId,
            serialNumber: form.value.serialNumber,
            type: form.value.type
        }
        : null;
});

const customColour = ref<string>('');
const customDealer = ref<string>('');
const dealerSelect = ref(null);

const useCustomDealer = ref<boolean>(false);

const productDetails = ref<{[productId: string]: ProductDetails}>({});
const retailers = ref<string[]>([]);

watch(() => activeProductOption.value, resetProduct);

function resetProduct() {
    if (props.editMode) {
        return;
    }
    if (form.value) {
        form.value.name = activeProductOption.value.displayName;
        form.value.type = activeProductOption.value.category;
        form.value.sapProductId = activeProductOption.value.productId;
        if (getColorOptions(activeProductOption.value).length) {
            selectedColor.value = getColorOptions(activeProductOption.value)[0];
        }
    } else if (form.value) {
        selectedColor.value = OTHER;
    }
}

function specifyCustomDealer() {
    (dealerSelect.value as any).searchEl.blur();
    form.value.productDealer = '';
    useCustomDealer.value = true;
}

function checkCustomDealer() {
    if (!customDealer.value) {
        form.value.productDealer = '';
        useCustomDealer.value = false;
    }
    if (retailers.value.indexOf(customDealer.value) > -1) {
        form.value.productDealer = customDealer.value;
        useCustomDealer.value = false;
    }
}

onMounted(async() => {
    const id = router.currentRoute.query.id as string;
    if (id) {
        editProductId.value = id;
        populateFormByEditFlow(id);
    } else {
        await populateFormByRegisterNewFlow();
    }
});

async function getColorsAndRetailers() {
    const hasOptions = productOptions.value.length;
    activeProductOption.value = hasOptions ? productOptions.value[0] : null;
    if (hasOptions) {
        const details = await productRegistrationService.getProductDetails(
            productOptions.value.map(searchResult => searchResult.productId));
        for (const product of details.products) {
            productDetails.value[product.productId] = product;
        }
        retailers.value = details.retailers;
    }
}

async function populateFormByRegisterNewFlow() {
    if (activeState.value) {
        await getColorsAndRetailers();

        form.value = activeState.value.request;
        resetProduct();
        updateDeliveryDate(form.value.dateBought);
    }
}

const emptyColorValue = { id: '', displayName: '' };

async function populateFormByEditFlow(id: string) {
    productRegistrationService.getProduct(id).then(async(data) => {
        editProduct.value = data.registeredProduct;
        editCatalogProduct.value = data.catalogProduct;

        if (data.images) {
            initialImages.value = data.images.map(ImageUploadService.mapUploadedImageForUpload);
            editImages.value = data.images.map(ImageUploadService.mapUploadedImageForUpload);
        }

        if (data.receipt) {
            const receipt: CarouselImageWithExtension = ImageUploadService.mapUploadedImageForUpload(data.receipt);
            initialReceipt.value = receipt;
            editReceipt.value = receipt;
        }

        const request: ProductRegistrationAddOrUpdateProductWebRequest = {
            name: editProduct.value.name,
            colour: editProduct.value.colour,
            comment: editProduct.value.comment,
            dateBought: editProduct.value.dateBought,
            productDealer: editProduct.value.productDealer,
            sapProductId: editProduct.value.sapProductId,
            serialNumber: editProduct.value.serialNumber,
            type: editProduct.value.type
        };

        await getColorsAndRetailers();

        if (!request.sapProductId || (request.colour &&
                                        productDetails.value[request.sapProductId] &&
                                        productDetails.value[request.sapProductId].colors.indexOf(request.colour) === -1)) {
            selectedColor.value = OTHER;
            customColour.value = request.colour;
        } else {
            selectedColor.value = request.colour ? { id: request.colour, displayName: request.colour } : emptyColorValue;
            customColour.value = '';
        }

        useCustomDealer.value = request.productDealer && retailers.value.indexOf(request.productDealer) === -1;
        if (useCustomDealer.value) {
            customDealer.value = request.productDealer;
            request.productDealer = null;
        } else {
            customDealer.value = '';
        }

        form.value = request;
        updateDeliveryDate(form.value.dateBought);
    });
}

const dateOfPurchaseValidation = ref<string | undefined>(undefined);

function updateDeliveryDate(input: Date) {
    dateOfPurchaseValidation.value = null;

    if (input) {
        dateOfPurchaseValidation.value = input.toString();
        form.value.dateBought = new Date(input.toString());
    } else {
        form.value.dateBought = null;
    }
}

function onReceiptEditChange(receipt: CarouselImageWithExtension) {
    editReceipt.value = receipt;
}

function onImagesEditChange(images: CarouselImageWithExtension[]) {
    editImages.value = images;

    // Update the imageFiles when editImages change
    const imagesComponent = imageUploadComponent.value as Vue;
    const imageInput: HTMLInputElement = imagesComponent.$refs.imageUploadFiles as HTMLInputElement;
    imageFiles.value = imageInput.files;
}

const submittingFormData = ref<boolean>(false);

function toIsoString(date) {
    const tzo = -date.getTimezoneOffset();
    date.setTime(date.getTime() + (tzo) * 60 * 1000);
    return date.toISOString();
}

function submitForm() {
    $validator.validateAll().then((isValid) => {
        if (!isValid) {
            return;
        }
        // submittingFormData.value = true;
        const formData: any = new FormData();

        for (const key of Object.keys(request.value)) {
            const value = key === 'dateBought' && request.value[key] ? toIsoString(request.value.dateBought) : request.value[key];
            if (value) {
                formData.append(key, value);
            }
        };

        if (receiptUploadComponent.value) {
            const receipt = receiptUploadComponent.value as Vue;
            const receiptInput: HTMLInputElement = receipt.$refs.receiptUploadFile as HTMLInputElement;
            for (let i = 0; i < receiptInput.files.length; i++) {
                formData.append('receiptUpload[]', receiptInput.files[i], receiptInput.files[i].name);
            }
        }
        if (imageUploadComponent.value) {
            const images = imageUploadComponent.value as Vue;
            const imageInput: HTMLInputElement = images.$refs.imageUploadFiles as HTMLInputElement;
            for (let i = 0; i < imageInput.files.length; i++) {
                formData.append('imageUpload[]', imageInput.files[i], imageInput.files[i].name);
            }
        }

        if (props.editMode) {
            const deletedImages = initialImages.value.filter(obj => !editImages.value.map(x => JSON.stringify(x)).includes(JSON.stringify(obj)));

            if (initialReceipt.value !== editReceipt.value) {
                deletedImages.push(initialReceipt.value);
            }

            formData.append('imagesToBeDeleted[]', JSON.stringify(deletedImages.filter(x => x.id).map(x => x.id)));

            productRegistrationService.submitUpdateProductRequest(formData as ProductRegistrationAddOrUpdateProductWebRequest, editProductId.value).then(
                () => {
                    submittingFormData.value = false;
                    productRegistrationActionService.SetActionContext(Action.Updated, '');
                    PageService.softRedirect(props.registeredProductPageUrl + '?id=' + editProductId.value);
                },
                () => {
                    submittingFormData.value = false;
                }
            );
        } else {
            productRegistrationService.submitAddProductRequest(formData as ProductRegistrationAddOrUpdateProductWebRequest).then(
                (product: Vertica.LouisPoulsen.Application.ProductRegistration.Responses.ProductViewObject) => {
                    submittingFormData.value = false;
                    productRegistrationService.deleteState();
                    productRegistrationActionService.SetActionContext(Action.Created, '');
                    PageService.softRedirect(props.registeredProductPageUrl + '?id=' + product.id);
                },
                () => {
                    submittingFormData.value = false;
                }
            );
        }
    });
}

// animation
function beforeEnter(el) { el.style.height = '0'; el.style.overflow = 'hidden'; }
function enter(el) { el.style.height = el.scrollHeight + 'px'; el.style.overflow = 'visible'; }
function beforeLeave(el) { el.style.height = el.scrollHeight + 'px'; el.style.overflow = 'hidden'; }
function leave(el) { el.style.height = '0'; el.style.overflow = 'hidden'; }


return { __sfc: true,props, t, OTHER, activeState, createdViaSerialNumber, productOptions, editProductId, editProduct, editCatalogProduct, initialReceipt, editReceipt, initialImages, editImages, receiptUploadComponent, imageUploadComponent, imageFiles, activeProductOption, getColorOptions, selectedColor, selectedProductDealer, title, $validator, form, topImageUrl, request, customColour, customDealer, dealerSelect, useCustomDealer, productDetails, retailers, resetProduct, specifyCustomDealer, checkCustomDealer, getColorsAndRetailers, populateFormByRegisterNewFlow, emptyColorValue, populateFormByEditFlow, dateOfPurchaseValidation, updateDeliveryDate, onReceiptEditChange, onImagesEditChange, submittingFormData, toIsoString, submitForm, beforeEnter, enter, beforeLeave, leave, ProductTileImageCarousel, ReceiptUpload, ImageUpload, CollapsibleContent }
}

})